import { CERBERUS_STATS_FIELD_CONFIGS } from '@hcs/cerberus-stats';
import { ChartTimeSeriesLine, Stats } from '@hcs/design-system';
import { NullState } from '@hcs/design-system';
import { ExperienceFlag } from '@hcs/experience-flags';
import { Glossary } from '@hcs/glossary';
import { GLOSSARY_TERMS } from '@hcs/glossary';
import { ChartDimensions, ChartSizes, LineChart } from '@hcs/pdf/charts';
import { CERBERUS_STATS_FIELDS } from '@hcs/types';
import { ReportId } from '@hcs/types';
import { formatNumber } from '@hcs/utils';

import { useMonthsOfSupplyChartDocument } from '../../hooks/useMonthsOfSupplyChartDocument';

import styles from './MonthsOfSupply.module.css';
const dataHcName = 'months-of-supply-chart';
interface Props {
  reportId: ReportId;
  chartSize?: ChartSizes;
  chartDimensions?: ChartDimensions;
  className?: string;
  reverseTickRotation?: boolean;
}
const {
  label,
  d3FormatStr,
  // Not using Display yet since @john is actively refactoring
} = CERBERUS_STATS_FIELD_CONFIGS[CERBERUS_STATS_FIELDS.monthsOfSupplyMedian];
export const MonthsOfSupplyChart = ({
  reportId,
  chartSize,
  chartDimensions,
  className,
  reverseTickRotation = false,
}: Props) => {
  const { isLoading, data: monthsOfSupplyDoc } =
    useMonthsOfSupplyChartDocument(reportId);
  if (!isLoading && !monthsOfSupplyDoc?.data?.chart.data?.length) {
    return (
      <NullState
        title="Months of Supply Unavailable"
        dataHcName={`${dataHcName}-null-state`}
        absCenter
      />
    );
  }
  return (
    <ExperienceFlag
      experienceFlagId="PEXP_REDESIGN"
      next={
        <ChartTimeSeriesLine
          rightAxisMargin={35}
          dataHcName={dataHcName}
          isLoading={isLoading}
          // setting +45 deg prevents first tick from being cut off in PEXP report page tab
          axisBottom={{ tickRotation: reverseTickRotation ? 45 : -40 }}
          chartData={[
            {
              id: 'days-on-market',
              data: monthsOfSupplyDoc?.data?.chart.data || [],
            },
          ]}
          title={label}
          d3FormatterForY={d3FormatStr}
          theme={{
            Container: className,
          }}
          compactTitle
          stats={[
            {
              label: 'This month last year',
              value: formatNumber(
                monthsOfSupplyDoc?.data?.chart.stats.median.lastYear,
                { precision: 1 },
              ),
              dataHcName: 'year-prev',
            },
            {
              label: 'This month',
              value: formatNumber(
                monthsOfSupplyDoc?.data?.chart.stats.median.current,
                { precision: 1 },
              ),
              dataHcName: 'year-current',
            },
          ]}
          statsStyle={styles.StatsMarginAdjust}
        />
      }
      now={
        <LineChart
          title={
            <>
              Months of Supply - ZIP
              <Glossary glossaryTerms={[GLOSSARY_TERMS.MonthsOfSupply]} />
            </>
          }
          dataHcName={dataHcName}
          chartSize={chartSize}
          chartDimensions={chartDimensions}
          chartData={monthsOfSupplyDoc?.data?.chart.data}
          yAxis={{ label: 'Months of Supply' }}
          isLoading={isLoading}
          contentBelow={
            <Stats
              className={styles.StatsMarginAdjust}
              dataHcName={`${dataHcName}-stats`}
              stats={[
                {
                  label: 'This month last year',
                  value: formatNumber(
                    monthsOfSupplyDoc?.data?.chart.stats.median.lastYear,
                  ),
                  dataHcName: 'year-prev',
                },
                {
                  label: 'This month',
                  value: formatNumber(
                    monthsOfSupplyDoc?.data?.chart.stats.median.current,
                  ),
                  dataHcName: 'year-current',
                },
              ]}
            />
          }
        />
      }
    />
  );
};
